// ---------------------------------------------------------------------------
// CATÁLOGO DE BLOCOS — um só descritor por bloco.
//
// UM objeto por tipo, e dentro dele TUDO: os campos do editor, o padrão e a
// validação. É a correção do defeito herdado: no FlyOps o schema de bloco está
// escrito três vezes (Zod no backend, interface TS no render, inferência por
// nome de chave no editor) e a divergência já produziu o bug "salvar não
// persiste", cuja cicatriz é o `.passthrough()` com merge no registry.
//
// Sem Zod de propósito: este módulo é copiado em três repositórios e um deles
// (o backend) não tem Zod. Dependência aqui quebraria a regra do módulo
// compartilhado. `validar` faz o mesmo trabalho: recusa o que é inválido com
// mensagem pronta para o usuário e devolve o objeto CANÔNICO.
//
// Duas regras que valem para todos:
//  1. `validar` devolve objeto novo, montado na ordem canônica. A ordem importa:
//     a gravação faz no-op por SHA-256 da string.
//  2. Campo desconhecido é DESCARTADO, não recusado — o editor de amanhã manda
//     chave que o servidor de hoje não conhece durante o deploy. Mas campo
//     conhecido com valor inválido é ERRO: silêncio aqui vira site quebrado.
// ---------------------------------------------------------------------------

import { EscopoDoSite } from "./tipos";
import { sanitizarHtml } from "./sanitizacao";

/** Como o editor desenha um campo. O editor não infere nada pelo nome. */
export type TipoDeCampo =
  | "texto"
  | "textoLongo"
  | "html"
  | "url"
  | "imagem"
  | "cor"
  | "booleano"
  | "numero"
  | "escolha"
  | "lista";

export interface CampoDoEditor {
  chave: string;
  rotulo: string;
  tipo: TipoDeCampo;
  /** Frase curta abaixo do campo. É onde a armadilha do campo é explicada. */
  ajuda?: string;
  opcoes?: { valor: string; rotulo: string }[];
  /** Só para `lista`: os campos de cada item. */
  itens?: CampoDoEditor[];
  minimo?: number;
  maximo?: number;
}

export interface DescritorDeBloco {
  tipo: string;
  rotulo: string;
  /** Uma linha no seletor de blocos do editor. */
  descricao: string;
  /** Em que páginas o bloco pode entrar. */
  escopos: EscopoDoSite[];
  /** Quantas vezes cabe na mesma página. `1` = só um (capa, rodapé). */
  maximoPorPagina: number;
  campos: CampoDoEditor[];
  padrao(): Record<string, unknown>;
  validar(bruto: unknown): Record<string, unknown>;
}

// --- auxiliares de validação -----------------------------------------------

function objeto(bruto: unknown, tipo: string): Record<string, unknown> {
  if (bruto === null || typeof bruto !== "object" || Array.isArray(bruto)) {
    throw new Error(`A configuração do bloco "${tipo}" precisa ser um objeto.`);
  }
  return bruto as Record<string, unknown>;
}

function texto(
  valor: unknown,
  rotulo: string,
  opcoes: { obrigatorio?: boolean; maximo?: number } = {},
): string {
  const limite = opcoes.maximo ?? 400;
  if (valor === undefined || valor === null || valor === "") {
    if (opcoes.obrigatorio) throw new Error(`"${rotulo}" é obrigatório.`);
    return "";
  }
  if (typeof valor !== "string") {
    throw new Error(`"${rotulo}" precisa ser texto.`);
  }
  const limpo = valor.trim();
  if (limpo.length > limite) {
    throw new Error(`"${rotulo}" passa de ${limite} caracteres.`);
  }
  return limpo;
}

/**
 * URL de link. Só http/https e, de propósito, `mailto:`/`tel:` — o Linktree que
 * esta landing substitui tem "Localização" e telefone entre os links.
 *
 * `javascript:` e `data:` ficam de fora: são XSS armazenado com o nome de link.
 */
export function urlSegura(valor: unknown, rotulo: string, obrigatorio = false): string {
  const bruto = texto(valor, rotulo, { obrigatorio, maximo: 2000 });
  if (!bruto) return "";

  const permitidos = ["http:", "https:", "mailto:", "tel:"];
  let protocolo: string;
  try {
    protocolo = new URL(bruto).protocol;
  } catch {
    throw new Error(`"${rotulo}" não é um endereço válido: ${bruto}`);
  }
  if (!permitidos.includes(protocolo)) {
    throw new Error(
      `"${rotulo}" usa um esquema que não é aceito (${protocolo}). ` +
        `Aceitos: ${permitidos.join(", ")}.`,
    );
  }
  return bruto;
}

/**
 * URL de imagem. Só http/https — `data:` aqui seria um jeito de guardar
 * megabytes de base64 dentro de uma coluna de configuração.
 */
function urlDeImagem(valor: unknown, rotulo: string): string {
  const bruto = texto(valor, rotulo, { maximo: 2000 });
  if (!bruto) return "";
  let protocolo: string;
  try {
    protocolo = new URL(bruto).protocol;
  } catch {
    throw new Error(`"${rotulo}" não é um endereço de imagem válido.`);
  }
  if (protocolo !== "http:" && protocolo !== "https:") {
    throw new Error(`"${rotulo}" precisa ser um endereço http ou https.`);
  }
  return bruto;
}

function booleano(valor: unknown, padrao: boolean): boolean {
  if (valor === undefined || valor === null) return padrao;
  return valor === true;
}

function escolha<T extends string>(
  valor: unknown,
  aceitos: readonly T[],
  padrao: T,
  rotulo: string,
): T {
  if (valor === undefined || valor === null || valor === "") return padrao;
  if (typeof valor !== "string" || !aceitos.includes(valor as T)) {
    throw new Error(
      `"${rotulo}" tem um valor que não existe: ${JSON.stringify(valor)}. ` +
        `Aceitos: ${aceitos.join(", ")}.`,
    );
  }
  return valor as T;
}

function inteiro(valor: unknown, minimo: number, maximo: number, padrao: number, rotulo: string): number {
  if (valor === undefined || valor === null || valor === "") return padrao;
  const numero = typeof valor === "number" ? valor : Number(valor);
  if (!Number.isInteger(numero) || numero < minimo || numero > maximo) {
    throw new Error(`"${rotulo}" precisa ser um inteiro entre ${minimo} e ${maximo}.`);
  }
  return numero;
}

function lista(valor: unknown, rotulo: string, maximo: number): unknown[] {
  if (valor === undefined || valor === null) return [];
  if (!Array.isArray(valor)) throw new Error(`"${rotulo}" precisa ser uma lista.`);
  if (valor.length > maximo) {
    throw new Error(`"${rotulo}" tem mais de ${maximo} itens.`);
  }
  return valor;
}

// --- o catálogo ------------------------------------------------------------

const ALTURAS = ["media", "alta"] as const;
const REDES = ["instagram", "facebook", "whatsapp", "tiktok", "youtube", "x"] as const;

export const DESCRITORES: DescritorDeBloco[] = [
  {
    tipo: "capa",
    rotulo: "Capa",
    descricao: "A primeira tela: logo, nome e a frase que resume o restaurante.",
    escopos: ["organization", "company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "frase", rotulo: "Frase", tipo: "textoLongo", ajuda: 'Ex.: "Desde 1982 alimentando o sentimento dos paulistanos apaixonados por pizza".' },
      { chave: "logoUrl", rotulo: "Logo", tipo: "imagem" },
      { chave: "imagemFundo", rotulo: "Imagem de fundo", tipo: "imagem", ajuda: "Foto do salão ou do prato. Sem ela, a capa usa a cor da marca." },
      { chave: "altura", rotulo: "Altura", tipo: "escolha", opcoes: [
        { valor: "media", rotulo: "Média" },
        { valor: "alta", rotulo: "Tela cheia" },
      ] },
    ],
    padrao: () => ({ titulo: "", frase: "", logoUrl: "", imagemFundo: "", altura: "media" }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      return {
        titulo: texto(cru.titulo, "Título", { maximo: 120 }),
        frase: texto(cru.frase, "Frase", { maximo: 400 }),
        logoUrl: urlDeImagem(cru.logoUrl, "Logo"),
        imagemFundo: urlDeImagem(cru.imagemFundo, "Imagem de fundo"),
        altura: escolha(cru.altura, ALTURAS, "media", "Altura"),
      };
    },
  },

  {
    tipo: "pedir",
    rotulo: "Peça agora",
    descricao: "O botão de pedir. Pergunta em qual unidade o pedido entra.",
    escopos: ["company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "rotulo", rotulo: "Texto do botão", tipo: "texto" },
      { chave: "chamada", rotulo: "Chamada acima do botão", tipo: "textoLongo" },
    ],
    padrao: () => ({ rotulo: "Peça agora", chamada: "", modo: "unidade-mais-proxima" }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      return {
        rotulo: texto(cru.rotulo, "Texto do botão", { maximo: 40 }) || "Peça agora",
        chamada: texto(cru.chamada, "Chamada", { maximo: 200 }),
        // Único modo por enquanto. O campo existe para o dia em que houver
        // "escolher na lista" sem geolocalização como preferência do dono.
        modo: "unidade-mais-proxima",
      };
    },
  },

  {
    tipo: "cardapio",
    rotulo: "Cardápio",
    descricao: "O cardápio de verdade, do sistema da loja — não um PDF no Drive.",
    escopos: ["company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "modo", rotulo: "Origem", tipo: "escolha", opcoes: [
        { valor: "catalogo", rotulo: "Cardápio do sistema" },
        { valor: "arquivo", rotulo: "Arquivo (PDF)" },
      ], ajuda: "O cardápio do sistema é o que a cozinha usa — muda sozinho quando a loja mexe no preço." },
      { chave: "arquivoUrl", rotulo: "Endereço do PDF", tipo: "url" },
    ],
    padrao: () => ({ titulo: "Cardápio", modo: "catalogo", arquivoUrl: "", destaqueCategorias: [] }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      const modo = escolha(cru.modo, ["catalogo", "arquivo"] as const, "catalogo", "Origem");
      const arquivoUrl = urlSegura(cru.arquivoUrl, "Endereço do PDF");
      if (modo === "arquivo" && !arquivoUrl) {
        throw new Error(
          'O cardápio está como "Arquivo (PDF)" e nenhum endereço foi informado — ' +
            "a seção ficaria vazia na landing.",
        );
      }
      const destaques = lista(cru.destaqueCategorias, "Categorias em destaque", 12)
        .map((item, indice) => texto(item, `Categoria em destaque ${indice + 1}`, { maximo: 80 }))
        .filter(Boolean);
      return { titulo: texto(cru.titulo, "Título", { maximo: 80 }) || "Cardápio", modo, arquivoUrl, destaqueCategorias: destaques };
    },
  },

  {
    tipo: "unidades",
    rotulo: "Unidades",
    descricao: "Onde ficam as lojas, com endereço, telefone e rota.",
    escopos: ["company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "mostrarMapa", rotulo: "Mostrar link do mapa", tipo: "booleano" },
    ],
    padrao: () => ({ titulo: "Onde estamos", mostrarMapa: true }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      return {
        titulo: texto(cru.titulo, "Título", { maximo: 80 }) || "Onde estamos",
        mostrarMapa: booleano(cru.mostrarMapa, true),
      };
    },
  },

  {
    tipo: "links",
    rotulo: "Links",
    descricao: "A lista de links que fica no Linktree hoje.",
    escopos: ["organization", "company"],
    maximoPorPagina: 2,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "itens", rotulo: "Links", tipo: "lista", maximo: 20, itens: [
        { chave: "rotulo", rotulo: "Texto", tipo: "texto" },
        { chave: "url", rotulo: "Endereço", tipo: "url" },
      ] },
    ],
    padrao: () => ({ titulo: "", itens: [] }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      const itens = lista(cru.itens, "Links", 20).map((item, indice) => {
        const linha = objeto(item, "links");
        return {
          rotulo: texto(linha.rotulo, `Texto do link ${indice + 1}`, { obrigatorio: true, maximo: 80 }),
          url: urlSegura(linha.url, `Endereço do link ${indice + 1}`, true),
        };
      });
      return { titulo: texto(cru.titulo, "Título", { maximo: 80 }), itens };
    },
  },

  {
    tipo: "redes",
    rotulo: "Redes sociais",
    descricao: "Instagram, Facebook, WhatsApp.",
    escopos: ["organization", "company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "itens", rotulo: "Perfis", tipo: "lista", maximo: 8, itens: [
        { chave: "rede", rotulo: "Rede", tipo: "escolha", opcoes: REDES.map((rede) => ({ valor: rede, rotulo: rede })) },
        { chave: "url", rotulo: "Endereço do perfil", tipo: "url" },
      ] },
    ],
    padrao: () => ({ itens: [] }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      const itens = lista(cru.itens, "Perfis", 8).map((item, indice) => {
        const linha = objeto(item, "redes");
        return {
          rede: escolha(linha.rede, REDES, "instagram", `Rede do perfil ${indice + 1}`),
          url: urlSegura(linha.url, `Endereço do perfil ${indice + 1}`, true),
        };
      });
      return { itens };
    },
  },

  {
    tipo: "avaliacoes",
    rotulo: "Avaliações",
    descricao: "A nota do Google, com o link que funciona.",
    escopos: ["company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "url", rotulo: "Endereço das avaliações", tipo: "url", ajuda: "Cole o link do perfil da loja no Google, não o endereço da busca — link de busca vence e para de funcionar." },
      { chave: "nota", rotulo: "Nota", tipo: "numero", minimo: 0, maximo: 5 },
      { chave: "total", rotulo: "Quantidade de avaliações", tipo: "numero" },
    ],
    padrao: () => ({ url: "", nota: 0, total: 0 }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      const url = urlSegura(cru.url, "Endereço das avaliações", true);
      // Nota é decimal (4,8) — o `inteiro` acima não serve.
      let nota = 0;
      if (cru.nota !== undefined && cru.nota !== null && cru.nota !== "") {
        nota = typeof cru.nota === "number" ? cru.nota : Number(cru.nota);
        if (!Number.isFinite(nota) || nota < 0 || nota > 5) {
          throw new Error('"Nota" precisa ser um número entre 0 e 5.');
        }
        nota = Math.round(nota * 10) / 10;
      }
      return { url, nota, total: inteiro(cru.total, 0, 1_000_000, 0, "Quantidade de avaliações") };
    },
  },

  {
    tipo: "texto",
    rotulo: "Texto",
    descricao: "Um trecho livre — a história da casa, um aviso.",
    escopos: ["organization", "company"],
    maximoPorPagina: 4,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "html", rotulo: "Texto", tipo: "html" },
    ],
    padrao: () => ({ titulo: "", html: "" }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      // ÚNICO bloco com HTML, e por isso o único que passa pela allowlist. O
      // valor gravado JÁ É o sanitizado: quem renderiza não precisa confiar.
      const html = typeof cru.html === "string" ? sanitizarHtml(cru.html) : "";
      if (html.length > 20_000) {
        throw new Error('"Texto" passa de 20.000 caracteres.');
      }
      return { titulo: texto(cru.titulo, "Título", { maximo: 80 }), html };
    },
  },

  {
    tipo: "galeria",
    rotulo: "Galeria",
    descricao: "Fotos do salão e dos pratos.",
    escopos: ["organization", "company"],
    maximoPorPagina: 2,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "colunas", rotulo: "Colunas", tipo: "numero", minimo: 2, maximo: 6 },
      { chave: "imagens", rotulo: "Fotos", tipo: "lista", maximo: 24, itens: [
        { chave: "url", rotulo: "Foto", tipo: "imagem" },
        { chave: "alt", rotulo: "Descrição da foto", tipo: "texto", ajuda: "Lida em voz alta por leitor de tela. Descreva o que está na foto." },
      ] },
    ],
    padrao: () => ({ titulo: "", colunas: 3, imagens: [] }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      const imagens = lista(cru.imagens, "Fotos", 24).map((item, indice) => {
        const linha = objeto(item, "galeria");
        const url = urlDeImagem(linha.url, `Foto ${indice + 1}`);
        if (!url) throw new Error(`A foto ${indice + 1} está sem endereço.`);
        return { url, alt: texto(linha.alt, `Descrição da foto ${indice + 1}`, { maximo: 160 }) };
      });
      return {
        titulo: texto(cru.titulo, "Título", { maximo: 80 }),
        colunas: inteiro(cru.colunas, 2, 6, 3, "Colunas"),
        imagens,
      };
    },
  },

  {
    tipo: "horarios",
    rotulo: "Horários",
    descricao: "Quando cada unidade abre.",
    escopos: ["company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "porUnidade", rotulo: "Separar por unidade", tipo: "booleano" },
    ],
    padrao: () => ({ titulo: "Horários", porUnidade: true }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      return {
        titulo: texto(cru.titulo, "Título", { maximo: 80 }) || "Horários",
        porUnidade: booleano(cru.porUnidade, true),
      };
    },
  },

  {
    tipo: "marcas",
    rotulo: "Restaurantes da rede",
    descricao: "Os cartões com link para a landing de cada marca.",
    escopos: ["organization"],
    maximoPorPagina: 1,
    campos: [
      { chave: "titulo", rotulo: "Título", tipo: "texto" },
      { chave: "chamada", rotulo: "Chamada", tipo: "textoLongo" },
    ],
    padrao: () => ({ titulo: "Nossos restaurantes", chamada: "" }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      return {
        titulo: texto(cru.titulo, "Título", { maximo: 80 }) || "Nossos restaurantes",
        chamada: texto(cru.chamada, "Chamada", { maximo: 200 }),
      };
    },
  },

  {
    tipo: "rodape",
    rotulo: "Rodapé",
    descricao: "Endereço, telefone e o crédito da casa.",
    escopos: ["organization", "company"],
    maximoPorPagina: 1,
    campos: [
      { chave: "copyright", rotulo: "Crédito", tipo: "texto" },
    ],
    // O selo do Alfredo é FIXO aqui e não é configurável — é a única aparição da
    // nossa marca na landing. A promessa do produto é "com a sua marca, não a de
    // um marketplace"; e ao mesmo tempo o crédito não some por configuração.
    padrao: () => ({ copyright: "" }),
    validar(bruto) {
      const cru = objeto(bruto, this.tipo);
      return { copyright: texto(cru.copyright, "Crédito", { maximo: 160 }) };
    },
  },
];

const POR_TIPO = new Map(DESCRITORES.map((descritor) => [descritor.tipo, descritor]));

export function descritorDeBloco(tipo: string): DescritorDeBloco | null {
  return POR_TIPO.get(tipo) ?? null;
}

export function blocosDoEscopo(escopo: EscopoDoSite): DescritorDeBloco[] {
  return DESCRITORES.filter((descritor) => descritor.escopos.includes(escopo));
}

/**
 * Valida e devolve o config canônico de um bloco.
 *
 * Tipo desconhecido é ERRO aqui (na gravação) e SILÊNCIO no render (o bloco
 * simplesmente não aparece). É de propósito: recusar na entrada evita lixo no
 * banco; ignorar na saída evita que um bloco de uma versão futura derrube a
 * landing inteira durante um deploy.
 */
export function validarConfigDeBloco(
  tipo: string,
  config: unknown,
  escopo?: EscopoDoSite,
): Record<string, unknown> {
  const descritor = descritorDeBloco(tipo);
  if (!descritor) {
    throw new Error(
      `Bloco de tipo "${tipo}" não existe. Tipos: ${DESCRITORES.map((d) => d.tipo).join(", ")}.`,
    );
  }
  if (escopo && !descritor.escopos.includes(escopo)) {
    throw new Error(
      `O bloco "${descritor.rotulo}" não pode entrar numa página de ${escopo}.`,
    );
  }
  return descritor.validar(config);
}
